import {
  FormLabel,
  IconButton,
  Input,
  useToast,
  HStack,
  VStack,
} from '@chakra-ui/react';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';
import { faCheck, faPen, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Project, UpdateProjectDocument } from '../../gql/graphql';
import SparkelIcon from '../common/icon/SparkelIcon';

type GeneralSettingsTabProps = {
  project: Pick<Project, 'id' | 'name' | 'settings'>;
};

export default function GeneralSettingsTab({
  project,
}: GeneralSettingsTabProps): React.ReactElement {
  const [isEditing, setIsEditing] = useState(false);

  const { register, handleSubmit, reset } = useForm<{ name: string }>({
    defaultValues: { name: project?.name },
  });

  const toast = useToast();

  const [, updateProject] = useMutation(UpdateProjectDocument);

  const onRenameSubmit = handleSubmit(async ({ name }) => {
    if (project && name !== project.name) {
      const result = await updateProject({
        input: { id: project.id, patch: { name } },
      });

      if (result.error) {
        toast({
          title: 'An error occurred when renaming project',
          status: 'error',
        });
        throw Error();
      } else {
        toast({
          title: 'Project successfully renamed',
          status: 'success',
        });
        setIsEditing(false);
      }
    } else {
      setIsEditing(false);
    }
  });

  return (
    <VStack alignItems={'start'} gap={4}>
      <VStack alignItems={'start'} gap={1}>
        <FormLabel>Project Name</FormLabel>
        <HStack>
          <form onSubmit={onRenameSubmit}>
            <Input
              {...register('name', {
                required: true,
              })}
              autoFocus
              variant={'filled'}
              borderRadius={'md'}
              isDisabled={!isEditing}
              width={400}
            />
          </form>
          {isEditing ? (
            <>
              <IconButton
                aria-label="Cancel renaming project"
                size="xs"
                colorScheme="gray"
                onClick={() => {
                  reset();
                  setIsEditing(false);
                }}
                isRound
                icon={<SparkelIcon icon={faTimes} color={'inherit'} />}
              />
              <IconButton
                aria-label="Save project name"
                size="xs"
                onClick={() => {
                  onRenameSubmit();
                  setIsEditing(false);
                }}
                isRound
                icon={<SparkelIcon icon={faCheck} color={'inherit'} />}
              />
            </>
          ) : (
            <IconButton
              aria-label="Rename project"
              colorScheme="gray"
              size="xs"
              onClick={() => setIsEditing(true)}
              isRound
              icon={<SparkelIcon icon={faPen} />}
            />
          )}
        </HStack>
      </VStack>
    </VStack>
  );
}
